import { Link } from 'react-router-dom';

const socialLinks = [
  {
    name: 'YouTube',
    handle: '@helpmediy',
    href: 'https://www.youtube.com/@helpmediy',
    className: 'youtube',
    eyebrow: 'Full walkthroughs',
    description: 'Follow the complete repair—from the first stubborn bolt to the moment everything works again.',
    action: 'Subscribe on YouTube',
  },
  {
    name: 'Instagram',
    handle: '@helpmediy_official',
    href: 'https://www.instagram.com/helpmediy_official/',
    className: 'instagram',
    eyebrow: 'Quick wins & behind the scenes',
    description: 'See what is on the lift, catch fast tips, and follow each project between the big videos.',
    action: 'Follow on Instagram',
  },
  {
    name: 'Facebook',
    handle: 'HelpMeDIY',
    href: 'https://www.facebook.com/HelpMeDIY',
    className: 'facebook',
    eyebrow: 'Projects worth sharing',
    description: 'Join the conversation, share a repair with a friend, and keep useful DIY knowledge moving.',
    action: 'Follow on Facebook',
  },
];

const About = () => {
  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="about-hero-copy">
          <span className="about-kicker">Wrench. Learn. Share.</span>
          <h2>Fix it yourself.<br /><em>Know why it worked.</em></h2>
          <p className="about-hero-lead">
            I’m Aaron—an engineer, lifelong problem solver, and the person behind Help Me DIY.
            I turn real garage projects into practical walkthroughs so the next intimidating repair
            feels a whole lot more possible.
          </p>
          <div className="about-hero-actions">
            <a href="https://www.youtube.com/@helpmediy" target="_blank" rel="noreferrer noopener" className="about-button primary">
              Watch Help Me DIY <span aria-hidden="true">▶</span>
            </a>
            <Link to="/parts" className="about-button secondary">Explore Porsche parts</Link>
          </div>
        </div>
        <div className="about-hero-mark" aria-label="Help Me DIY">
          <div className="about-logo-orbit">
            <div className="about-garage-badge" aria-hidden="true">
              <span>HELP ME</span>
              <strong>DIY</strong>
              <small>GARAGE • GUIDES • COMMUNITY</small>
            </div>
            <span className="orbit-note top">Real projects</span>
            <span className="orbit-note right">Clear steps</span>
            <span className="orbit-note bottom">No mystery bolts</span>
          </div>
        </div>
      </section>

      <section className="about-manifesto">
        <div className="about-section-label">Why Help Me DIY exists</div>
        <div className="about-manifesto-copy">
          <h3>The best repair is the one that teaches you something.</h3>
          <p>
            Cars are complicated—but they are not magic. With the right information, a little patience,
            and permission to pause the video twelve times, you can do more than you think.
          </p>
          <p>
            Help Me DIY is where I document the wins, the surprises, the specialty tools, and the
            “well, that was harder than it looked” moments. The goal is simple: show the work honestly,
            explain the why, and leave behind something useful for the next person in the garage.
          </p>
        </div>
      </section>

      <section className="about-bench">
        <div className="about-bench-heading">
          <span className="about-kicker">What you’ll find here</span>
          <h3>Useful content, with grease under its fingernails.</h3>
        </div>
        <div className="about-bench-grid">
          <article>
            <span className="about-card-number">01</span>
            <h4>Real repairs</h4>
            <p>Step-by-step jobs on the cars actually in the garage—not a perfect studio engine that was loosened beforehand.</p>
          </article>
          <article>
            <span className="about-card-number">02</span>
            <h4>Porsche deep dives</h4>
            <p>Parts diagrams, Service Specs, rebuilds, upgrades, and the details that make a 987 project go more smoothly.</p>
          </article>
          <article>
            <span className="about-card-number">03</span>
            <h4>Confidence to start</h4>
            <p>Clear explanations that help you decide what belongs on your DIY list—and when calling a pro is the smart move.</p>
          </article>
        </div>
      </section>

      <section className="about-social-section">
        <div className="about-social-heading">
          <span className="about-kicker">Come hang out</span>
          <h3>One channel. Three good reasons to follow.</h3>
          <p>The full repair, the quick update, and the conversation afterward all live in different places. Pick your favorite—or join me on all three.</p>
        </div>
        <div className="about-social-grid">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              className={`about-social-card ${social.className}`}
              href={social.href}
              target="_blank"
              rel="noreferrer noopener"
            >
              <div className="about-social-topline">
                <span>{social.eyebrow}</span>
                <strong aria-hidden="true">↗</strong>
              </div>
              <h4>{social.name}</h4>
              <div className="about-social-handle">{social.handle}</div>
              <p>{social.description}</p>
              <span className="about-social-action">{social.action}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="about-community-callout">
        <div>
          <span className="about-kicker">This works better together</span>
          <h3>Watch a project. Share what you know. Help the next person turn the key.</h3>
        </div>
        <div className="about-community-actions">
          <a href="https://www.youtube.com/@helpmediy" target="_blank" rel="noreferrer noopener" className="about-button primary">Join the YouTube community</a>
          <Link to="/parts" className="about-button secondary">Browse Parts &amp; Diagrams</Link>
        </div>
      </section>
    </main>
  );
};

export default About;
